// src/pages/AuthPage.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import logo from "../assets/logo.png";
import LoginForm from "../components/LoginForm";
import SignupForm from "../components/SignupForm";
import "../styles/Auth.css";
import { motion } from "framer-motion";
import AuthNav from "../components/AuthNav";
import { useNavigate } from "react-router-dom";

function AuthPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);

  // 只用 auth 命名空间
  const { t: tAuth, i18n } = useTranslation("auth");

  // ✅ 统一的语言切换函数：修改 i18n + 写入 localStorage + 更新 URL ?lang
  const switchLanguage = function (lang) {
    // 只接受 zh / en
    var finalLang = lang === "en" ? "en" : "zh";

    // 1) 切换 i18n 语言
    if (i18n && typeof i18n.changeLanguage === "function") {
      i18n.changeLanguage(finalLang);
    }

    // 2) 保存到 localStorage，方便其它页面初始化
    try {
      localStorage.setItem("selectedLang", finalLang);
    } catch (eStore) {
      console.warn("AuthPage: save selectedLang failed:", eStore);
    }

    // 3) 把 ?lang=xx 写回当前 URL，保证从这里跳出去再回来还能保持语言
    try {
      var url = new URL(window.location.href);
      url.searchParams.set("lang", finalLang);
      window.history.replaceState({}, document.title, url.toString());
    } catch (eUrl) {
      console.warn("AuthPage: update lang in url failed:", eUrl);
    }
  };

  // ✅ 初始化语言：
  // 1. 如果 URL 里有 ?lang=xx，优先按 URL
  // 2. 否则用 localStorage.selectedLang
  // 3. 最后默认 zh
  useEffect(
    function () {
      var initialLang = "zh";

      try {
        // ① URL 查询参数
        var search = window.location.search || "";
        var hasUrlLang = false;

        if (search) {
          var params = new URLSearchParams(search);
          var urlLang = params.get("lang");
          if (urlLang === "zh" || urlLang === "en") {
            initialLang = urlLang;
            hasUrlLang = true;
          }
        }

        // ② 如果 URL 没给出合法 lang，再看 localStorage
        if (!hasUrlLang) {
          try {
            var savedLang = localStorage.getItem("selectedLang");
            if (savedLang === "zh" || savedLang === "en") {
              initialLang = savedLang;
            }
          } catch (eStore) {
            console.warn("AuthPage: read selectedLang failed:", eStore);
          }
        }
      } catch (e) {
        console.warn("AuthPage: init language failed:", e);
      }

      if (initialLang !== "zh" && initialLang !== "en") {
        initialLang = "zh";
      }

      // ③ 如果当前 i18n 语言不同，则切一次
      if (i18n && i18n.language !== initialLang) {
        i18n.changeLanguage(initialLang);
      }

      // ④ 同步一份到 localStorage 和 URL，保持全站一致
      switchLanguage(initialLang);
    },
    [i18n]
  );

  return (
    <div className="auth-background">
      {/* 顶部导航：Members / About */}
      <AuthNav />

      {/* 整个登录/注册卡片的进出场动画 */}
      <motion.div
        className="auth-page-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.16 }}
      >
        {/* 登录 / 注册 tab 切换 */}
        <div className="tab-switcher">
          <button
            className={"tab-button " + (isLogin ? "active" : "")}
            onClick={function () {
              setIsLogin(true);
            }}
          >
            {tAuth("login")}
          </button>
          <button
            className={"tab-button " + (!isLogin ? "active" : "")}
            onClick={function () {
              setIsLogin(false);
            }}
          >
            {tAuth("signup")}
          </button>
        </div>

        {/* Logo */}
        <div className="logo-container">
          <img src={logo} alt="Riceroma Logo" className="auth-logo" />
        </div>

        {/* 表单卡片 */}
        <div className="card">
          {isLogin ? (
            <>
              <LoginForm switchLanguage={switchLanguage} />
              {/* 忘记密码入口，仅在登录模式下显示 */}
              <div className="auth-forgot-row">
                <button
                  type="button"
                  className="auth-link-button"
                  onClick={function () {
                    navigate("/forgot-password");
                  }}
                >
                  忘记密码？ / Forgot password?
                </button>
              </div>
            </>
          ) : (
            <SignupForm
              showConfirmPassword={true}
              switchLanguage={switchLanguage}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default AuthPage;
